import {
  Directive,
  effect,
  inject,
  Injectable,
  Injector,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ScrollerOptions } from 'primeng/api';
import { SelectChangeEvent } from 'primeng/select';
import { IPrimeNgFilterRequest, ITableResponse } from '../model';
import { BaseHttpService } from '../services';
import { FunctionType } from '../typings';
import { finalize } from 'rxjs';

@Directive()
export class SelectService<T> implements OnInit {
  ngControl?: NgControl | null;

  private selectedValueRequests = new Set<string>();
  private defaultFiltersInitialized = false;
  private lastDefaultFilters = '';

  constructor(private injector: Injector) {
    effect(() => {
      if (this.virtualScroll() && this.api_url()) {
        this.getData();
      }
    });
    effect(() => {
      if (!this.virtualScroll() || !this.api_url()) {
        this.resetDefaultFilterTracking();
        return;
      }
      const serialized =
        JSON.stringify(this.defaultSearchFieldName() ?? {}) ?? '';
      if (!this.defaultFiltersInitialized) {
        this.defaultFiltersInitialized = true;
        this.lastDefaultFilters = serialized;
        return;
      }
      if (serialized === this.lastDefaultFilters) {
        return;
      }
      this.lastDefaultFilters = serialized;
      this.resetLazyOptions();
      this.getData();
    });
  }
  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null, { self: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  placeholder = input('');

  showClear = input(false);

  styleClass = input('w-full h-full');

  filterFields = input('');

  inputOptions = input<T[] | undefined>();

  isTranslated = input(false);

  isDisabled = signal(false);
  disabled = input<boolean>(false);

  first = signal<number>(0);

  api_url = input('');

  optionLabel = input('label');

  searchFieldName = input('search');

  defaultSearchFieldName = input<{ [key: string]: { value: string } }>();

  optionValue = input('value');

  data = signal<T[]>([]);

  hasMore = signal<boolean>(true);

  filterValue = signal('');

  virtualScroll = input(false);

  onChange: FunctionType = () => {};

  onTouched: FunctionType = () => {};

  changeValue = output<SelectChangeEvent>();

  label = input<string>();

  value = signal<T | null>(null);

  isMultiLang = input<boolean>(false);

  $baseHttpService = inject(BaseHttpService);

  options: ScrollerOptions = {
    delay: 250,
    showLoader: false,
    showSpacer: true,
    onScroll: this.scroll.bind(this),
  };

  tableData = signal<IPrimeNgFilterRequest>({
    first: 0,
    rows: 20,
    filters: '{}',
    sortOrder: 0,
  });

  scroll() {
    if (!this.api_url()) {
      return;
    }
    const panel = document.querySelector('.p-virtualscroller');
    if (panel) {
      const scrollTop = panel.scrollTop;
      const scrollHeight = panel.scrollHeight;
      const clientHeight = panel.clientHeight;
      if (scrollTop + clientHeight >= scrollHeight - 5 && this.hasMore()) {
        this.options.showLoader = true;
        this.first.set(this.first() + 20);
        this.getData();
      }
    }
  }

  change($event: SelectChangeEvent) {
    if (Object.is(this.value(), $event.value)) {
      return;
    }
    this.value.set($event.value);
    this.onChange($event.value);
    this.changeValue.emit($event);
  }

  getValueWithFilter() {
    if (!this.api_url()) {
      return;
    }
    this.tableData.update((el) => {
      el.rows = 20;
      return el;
    });
    this.data.set([]);
    this.first.set(0);
    this.getData();
  }

  getData() {
    if (!this.api_url()) {
      return;
    }
    let filters = {};

    if (this.filterValue()) {
      filters = {
        [this.searchFieldName()]: {
          value: this.filterValue(),
        },
      };
    }

    if (this.defaultSearchFieldName()) {
      filters = {
        ...filters,
        ...this.defaultSearchFieldName(),
      };
    }

    const filter = {
      ...this.tableData(),
      first: this.first(),
      filters: JSON.stringify(filters),
    };
    this.$baseHttpService
      .post<ITableResponse<T>>(this.api_url(), filter)
      .subscribe((res) => {
        this.hasMore.set(this.data().length < res.result.total);
        this.mergeOptions(res.result.items ?? []);
      });
  }

  writeValue(data: T): void {
    this.value.set(data);
    if (!this.virtualScroll() || !this.api_url()) {
      return;
    }
    if (data === null || data === undefined) {
      return;
    }
    const castedData = data as unknown;
    if (Array.isArray(castedData)) {
      if (!castedData.length) {
        return;
      }
      castedData.forEach((val) => this.ensureSelectedOptionLoaded(val));
      return;
    }
    this.ensureSelectedOptionLoaded(castedData);
  }
  registerOnChange(fn: FunctionType): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: FunctionType): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    // Only update if disabled input is not explicitly set
    // This allows the disabled input to take precedence
    if (this.disabled() === false) {
      this.isDisabled.set(isDisabled);
    }
  }

  private mergeOptions(items: T[], prepend = false) {
    if (!items?.length) {
      return;
    }
    const combined = prepend
      ? [...items, ...this.data()]
      : [...this.data(), ...items];
    this.data.set(this.dedupeOptions(combined));
  }

  private dedupeOptions(items: T[]): T[] {
    const seen = new Set<string>();
    const optionKey = this.optionValue();
    const unique: T[] = [];
    items.forEach((item) => {
      if (!item) {
        return;
      }
      const record = item as Record<string, unknown>;
      const value = record?.[optionKey];
      const normalized = this.normalizeValue(value);
      if (normalized) {
        if (seen.has(normalized)) {
          return;
        }
        seen.add(normalized);
      }
      unique.push(item);
    });
    return unique;
  }

  private normalizeValue(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const normalized = String(value);
    return normalized.length ? normalized : null;
  }

  private hasOption(value: unknown): boolean {
    const normalized = this.normalizeValue(value);
    if (!normalized) {
      return false;
    }
    return this.data().some((item) => {
      const record = item as Record<string, unknown>;
      const optionValue = record?.[this.optionValue()];
      return this.normalizeValue(optionValue) === normalized;
    });
  }

  // Ensure that lazy selects display pre-selected values by preloading the option.
  private ensureSelectedOptionLoaded(value: unknown) {
    const normalized = this.normalizeValue(value);
    if (
      !normalized ||
      !this.virtualScroll() ||
      !this.api_url() ||
      this.hasOption(value) ||
      this.selectedValueRequests.has(normalized)
    ) {
      return;
    }
    this.selectedValueRequests.add(normalized);
    const payload = {
      first: 0,
      rows: 1,
      filters: JSON.stringify({
        [this.optionValue()]: {
          value,
        },
      }),
    };

    this.$baseHttpService
      .post<ITableResponse<T>>(this.api_url(), payload)
      .pipe(finalize(() => this.selectedValueRequests.delete(normalized)))
      .subscribe((res) => {
        const items = res.result.items ?? [];
        if (items.length) {
          this.mergeOptions(items, true);
        }
      });
  }

  private resetLazyOptions() {
    this.data.set([]);
    this.first.set(0);
    this.hasMore.set(true);
    this.selectedValueRequests.clear();
  }

  private resetDefaultFilterTracking() {
    this.defaultFiltersInitialized = false;
    this.lastDefaultFilters = '';
  }
}
